/**
 * Object Folder Expansion - Handles loading database objects when folders are expanded
 */

import {
  DatabaseExplorerNode,
  FolderTreeNode,
  createDatabaseObjectNode,
} from '../../explorer/models/DatabaseTreeModels';
import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import {
  createEmptyMessageNode,
  createErrorNode,
} from './objectFolderUtils';

export interface LoadObjectFolderOptions {
  node: FolderTreeNode;
  connectionId: string;
  database?: string;
  objectType: DatabaseObjectType;
  fetchObjectsForFolder: (
    objectType: DatabaseObjectType,
    connectionId: string,
    database: string,
  ) => Promise<DatabaseObject[]>;
  addDatabaseObjects: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  setLoadingObject: (connectionId: string, database: string, objectType: string, isLoading: boolean) => void;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  getPreloadedObjects: (connectionId: string, database: string) => DatabaseObject[] | undefined;
  /** Called when a fetch error occurs - can be used to detect connection loss */
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  /**
   * "The connect this expand was started for is over." Captured before the
   * listing and checked when it returns: the tree underneath now belongs to the
   * SUCCESSOR connection, so an obsolete success would overwrite its folder and
   * an obsolete failure — including the rejection that ending a connect causes
   * by cancelling its queries — would write an error + Retry tile into a
   * connection that is already gone.
   */
  isStale?: () => boolean;
}

export async function loadObjectFolder({
  node,
  connectionId,
  database,
  objectType,
  fetchObjectsForFolder,
  addDatabaseObjects,
  setLoadingObject,
  updateNode,
  getPreloadedObjects,
  onFetchError,
  isStale,
}: LoadObjectFolderOptions) {
  if (!database) {
    updateNode(node.id, {
      children: [
        {
          id: `error-${connectionId}-unknown-${objectType}`,
          parentId: node.id,
          name: 'Error: No database defined',
          type: 'error',
          canHaveChildren: false,
          children: [],
          isExpanded: false,
          metadata: {
            connectionId,
            error: 'No database defined',
          },
        },
      ],
      isLoading: false,
    });
    return;
  }

  const schemaFilter = (node.metadata as { schema?: string }).schema;
  const applySchemaFilter = (objs: DatabaseObject[]): DatabaseObject[] =>
    schemaFilter ? objs.filter((o) => o.schema === schemaFilter) : objs;

  const preloaded = getPreloadedObjects(connectionId, database);
  if (preloaded && preloaded.length > 0) {
    const typeObjects = applySchemaFilter(preloaded.filter((obj) => obj.type === objectType));

    if (typeObjects.length > 0) {
      updateNode(node.id, {
        children: typeObjects.map((obj) => createDatabaseObjectNode(obj, node.id)),
        isLoading: false,
      });
      return;
    }
  }

  setLoadingObject(connectionId, database, objectType, true);
  updateNode(node.id, { isLoading: true });

  try {
    const rawObjects = await fetchObjectsForFolder(objectType, connectionId, database);
    // The listing outlived the connect that asked for it: the tree below is the
    // SUCCESSOR connection's, so these rows would overwrite its folder.
    if (isStale?.()) return;
    const objects = applySchemaFilter(rawObjects);

    if (objects.length === 0) {
      updateNode(node.id, {
        children: [createEmptyMessageNode(connectionId, database, objectType, node.id)],
        isLoading: false,
      });
      return;
    }

    addDatabaseObjects(connectionId, database, rawObjects);
    updateNode(node.id, {
      children: objects.map((obj) => createDatabaseObjectNode(obj, node.id)),
      isLoading: false,
    });
  } catch (error) {
    // Same fence on the failure path — and BEFORE `onFetchError`, which drives
    // connection-lost detection: a dead connect's error must not mark the live
    // connection down.
    if (isStale?.()) return;
    const message = error instanceof Error ? error.message : undefined;

    // Notify caller of error (for connection loss detection)
    if (message) {
      onFetchError?.(connectionId, message);
    }

    updateNode(node.id, {
      children: [createErrorNode(connectionId, database, objectType, node.id, message)],
      isLoading: false,
    });
  } finally {
    // The loading flag is per (connection, database, objectType) and the
    // successor uses the same key, so only the owner may lower it.
    if (!isStale?.()) setLoadingObject(connectionId, database, objectType, false);
  }
}
